/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Card,
  Box,
  Stack,
  Typography,
  Tooltip,
  CircularProgress,
  Button as MuiButton,

  Collapse,
  Fade,
  Table,
  TableBody,
  TableRow,
  Zoom } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button, Modal, Snackbar } from 'Components';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import { axios, dataFetcher, endpoints } from 'Api';
import InputRow from 'Pages/private/settings/connected-accounts/components/InputRow';
import { Box as InnerBox, DialogContent, TableBodyCell } from 'Pages/private/settings/connected-accounts/components/CustomDesigns';
import UserRequest from 'Pages/private/settings/connected-accounts/components/UserRequest';
import AddBox from 'Pages/private/settings/connected-accounts/components/AddBox';
import GroupTableHead from 'Pages/private/settings/connected-accounts/components/GroupTableHead';
import GroupRow from 'Pages/private/settings/connected-accounts/components/GroupRow';
import PersonButton from 'Pages/private/settings/connected-accounts/components/PersonButton';
import AddSuccessorModal from 'Pages/private/settings/connected-accounts/Modals/AddSuccessorModal';
import styles from 'Pages/private/settings/connected-accounts/index.module.css';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';

const defaultSnackbar = { isVisible: false, type: undefined, message: undefined };

export const ConnectedAccountSettingsV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, setAppSnackbar } = useContext(AppContext);

  const [snackbar, setSnackbar] = useState(defaultSnackbar);
  const [userConnections, setUserConnections] = useState([]);
  const [userManagementModalIsVisible, setUserManagementModalVisibility] = useState(false);
  const [secessionAccount, setSecessionAccount] = useState(false);
  const [addViewerModalIsVisible, setAddViewerModalVisibility] = useState(false);
  const [addViewerModalSettings, updateAddViewerModalSettings] = useState({ title: '', description: '' });
  const [accessRequests, setAccessRequests] = useState(undefined);
  const [accessRequestsNotAccepted, setAccessRequestsNotAccepted] = useState(undefined);
  const [groups, setGroups] = useState(undefined);
  // Accepted viewers + pending requests are shown in one combined table,
  // filtered by these pills.
  const [connFilter, setConnFilter] = useState('all');
  const [viewerData, setViewerData] = useState([{ email: '', firstName: '', laseName: '' }]);

  const handalData = (row) => {
    const rows = [...viewerData];
    rows[row.idx] = { ...row };
    if (row.idx + 1 === viewerData.length) {
      rows.push({ email: '', firstName: '', laseName: '' });
    }
    setViewerData(rows);
  };

  const handalRemoveThisRow = (rowIdx) => {
    const oldRow = [...viewerData];
    const newRow = oldRow.filter((row) => row.idx !== rowIdx).map((row, index) => ({ ...row, idx: index }));
    setViewerData([]);
    setTimeout(() => setViewerData(newRow), 100);
  };

  const { isValidating: addingViewers, mutate: addViewers } = useSWR(
    [endpoints.addViewer, viewerData.filter((data) => data.idx !== undefined)],
    {
      fetcher: (url, viewers) => dataFetcher(url, { data: viewers }),
      onSuccess: ({ success }) => {
        if (success) setSnackbar({ isVisible: true, message: 'Viewers added successfully' });
        else setSnackbar({ isVisible: true, message: 'Oops! Something went wrong', type: 'error' });
      },
      onError: () => setSnackbar({ isVisible: true, message: 'Oops! Something went wrong', type: 'error' }),
    },
  );

  const addViewer = async () => {
    addViewers();
    setAddViewerModalVisibility(false);
    setUserManagementModalVisibility(false);
    setAppSnackbar({ message: 'Viewer added successfully', isVisible: true });
  };

  const { isValidating: gettingUserConnections, mutate: getUserConnections } = useSWR(
    [endpoints.getAllViewRequestSent, 1, 'null'],
    {
      fetcher: (url, status) => dataFetcher(url, { status }),
      onSuccess: ({ success, data }) => {
        if (success) setUserConnections(data);
        else setUserConnections([]);
      },
      onError: () => setUserConnections([]),
    },
  );

  const { isValidating: loadingAccessRequests, mutate: fetchAccessRequests } = useSWR(
    [endpoints.getAllViewRequest],
    {
      fetcher: (url, status) => dataFetcher(url, { status }),
      onSuccess: ({ success, data }) => {
        if (success && data) {
          setAccessRequests(data?.filter(({ status }) => status === '1'));
          setAccessRequestsNotAccepted(data?.filter(({ status }) => status === '0'));
        } else {
          setAccessRequests([]);
          setAccessRequestsNotAccepted([]);
        }
      },
      onError: () => {
        setAccessRequests([]);
        setAccessRequestsNotAccepted([]);
      },
    },
  );

  const { isValidating: loadingGroups, mutate: fetchGroups } = useSWR(endpoints.getMyGroups, {
    onSuccess: ({ success, data }) => {
      if (success && data) setGroups(data);
      else setGroups([]);
    },
    onError: () => setGroups([]),
  });

  useEffect(() => { fetchAccessRequests(); }, []);
  useEffect(() => {
    if (userManagementModalIsVisible) {
      fetchAccessRequests();
      fetchGroups();
    } else {
      setAddViewerModalVisibility(false);
      setGroups(undefined);
    }
  }, [userManagementModalIsVisible, fetchAccessRequests, fetchGroups]);
  useEffect(() => {
    if (secessionAccount) {
      fetchAccessRequests();
      fetchGroups();
    } else {
      setAddViewerModalVisibility(false);
      setGroups(undefined);
    }
  }, [secessionAccount, fetchAccessRequests, fetchGroups]);
  useEffect(() => {
    if (!addViewerModalIsVisible) updateAddViewerModalSettings({ title: '', description: '' });
  }, [addViewerModalIsVisible]);
  useEffect(() => { getUserConnections(); }, [getUserConnections]);

  return (
    <>
      <V2SettingsShell
        title="Connected accounts"
        description="Everyone connected to your account — accepted viewers and pending requests, in one place."
        fitViewport
      >
        {(() => {
          const accepted = accessRequests || [];
          const pending = accessRequestsNotAccepted || [];
          const filters = [
            { key: 'all', label: 'All', count: accepted.length + pending.length },
            { key: 'accepted', label: 'Accepted', count: accepted.length },
            { key: 'pending', label: 'Pending', count: pending.length },
          ];
          const showAccepted = connFilter === 'all' || connFilter === 'accepted';
          const showPending = connFilter === 'all' || connFilter === 'pending';
          const nothing = !loadingAccessRequests && accepted.length === 0 && pending.length === 0;
          return (
            <Card sx={{ p: { xs: 2, md: 3 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              {/* Toolbar: filter pills + add / succession actions. */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                sx={{ justifyContent: 'space-between', alignItems: { md: 'center' }, mb: 2, flexShrink: 0 }}
              >
                <Stack direction="row" spacing={0.5} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 999, alignSelf: { xs: 'flex-start', md: 'auto' } }}>
                  {filters.map((f) => {
                    const active = connFilter === f.key;
                    return (
                      <MuiButton
                        key={f.key}
                        size="small"
                        disableElevation
                        onClick={() => setConnFilter(f.key)}
                        variant={active ? 'contained' : 'text'}
                        sx={{ borderRadius: 999, minWidth: 0, px: 1.5, textTransform: 'none', fontWeight: 600, color: active ? undefined : 'text.secondary' }}
                      >
                        {f.label}
                        <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 700 }}>{f.count}</Box>
                      </MuiButton>
                    );
                  })}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }} useFlexGap flexWrap="wrap">
                  <Tooltip title="Name a successor who can preserve your videos for future generations." arrow>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={() => setSecessionAccount(true)}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                      Succession plan
                    </MuiButton>
                  </Tooltip>
                  <MuiButton
                    variant="contained"
                    size="small"
                    disableElevation
                    startIcon={<AddRoundedIcon />}
                    onClick={() => setUserManagementModalVisibility(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, whiteSpace: 'nowrap' }}
                  >
                    Add viewer
                  </MuiButton>
                </Stack>
              </Stack>

              {/* Combined list — fills the space between the header and the
                  bottom shortcut pills, scrolling internally. */}
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                {loadingAccessRequests ? (
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', color: 'text.secondary', py: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Getting user connections…</Typography>
                  </Stack>
                ) : nothing ? (
                  <Stack spacing={1} sx={{ alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', py: 6 }}>
                    <Typography variant="body2">No connected accounts yet.</Typography>
                    <MuiButton variant="contained" size="small" disableElevation startIcon={<AddRoundedIcon />} onClick={() => setUserManagementModalVisibility(true)} sx={{ textTransform: 'none' }}>
                      Add your first viewer
                    </MuiButton>
                  </Stack>
                ) : (
                  <Stack spacing={2}>
                    {showAccepted && accepted.length > 0 && (
                      <Box>
                        {connFilter === 'all' && (
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                            Accepted viewers
                          </Typography>
                        )}
                        <Box className={styles.addViewerData}>
                          {accepted.map((data) => (
                            <PersonButton key={data?.id} fetchAccessRequests={fetchAccessRequests} data={data} />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {showPending && pending.length > 0 && (
                      <Box>
                        {connFilter === 'all' && (
                          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                            Pending requests
                          </Typography>
                        )}
                        {pending.map(({ id, firstname, lastname, email }) => (
                          <UserRequest key={id} id={id} setSnackbar={setSnackbar} firstname={firstname} email={email} lastname={lastname} />
                        ))}
                      </Box>
                    )}
                    {showAccepted && !showPending && accepted.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>No accepted viewers.</Typography>
                    )}
                    {showPending && !showAccepted && pending.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>No pending requests.</Typography>
                    )}
                  </Stack>
                )}
              </Box>
            </Card>
          );
        })()}
      </V2SettingsShell>
      {/* MUI v5: `disableBackdropClick` was removed (see v5 component-changes migration guide) */}
      <Modal
        cancelButton="Cancel"
        cancelButtonClassName={styles.modalCancelButton}
        continueButton="Add"
        continueButtonClassName={styles.modalContinueButton}
        isVisible={userManagementModalIsVisible}
        noFooter={!addViewerModalIsVisible}
        onContinueButtonClick={addViewer}
        onCancelButtonClick={() => setAddViewerModalVisibility(false)}
        onHide={() => setUserManagementModalVisibility(false)}
        title={addViewerModalIsVisible ? addViewerModalSettings.title : 'Add Viewer | Accept User'}
      >
        {!addViewerModalIsVisible ? (
          <>
            <div className={styles.acceptUserBox}>
              <Collapse timeout={400} in={loadingAccessRequests}>
                <Zoom timeout={400} in={loadingAccessRequests}>
                  <InnerBox className={styles.contentBox}>
                    <CircularProgress />
                  </InnerBox>
                </Zoom>
              </Collapse>
              <Collapse timeout={400} in={!loadingAccessRequests && !!accessRequestsNotAccepted?.length}>
                <Zoom timeout={400} in={!loadingAccessRequests && !!accessRequestsNotAccepted?.length}>
                  <>
                    <div className={styles.acceptUserBoxTitle}>Accept User</div>
                    <div className={styles.acceptUserBoxDescription}>
                      All user requests will appear below, so you can decide to accept or decline
                    </div>
                    {accessRequestsNotAccepted?.map(({ id, firstname, lastname, email }) => (
                      <UserRequest
                        key={id}
                        id={id}
                        setSnackbar={setSnackbar}
                        firstname={firstname}
                        email={email}
                        lastname={lastname}
                      />
                    ))}
                  </>
                </Zoom>
              </Collapse>
              <Collapse timeout={400} in={!loadingAccessRequests && !accessRequestsNotAccepted?.length}>
                <Zoom timeout={400} in={!loadingAccessRequests && !accessRequestsNotAccepted?.length}>
                  <InnerBox className={styles.contentBox}>No view requests</InnerBox>
                </Zoom>
              </Collapse>
            </div>
            <hr className={styles.modalDivider} />
            <div className={styles.addBoxContainer}>
              <AddBox
                description="Add Multiple users to this group. All users in this group Will have access to your uploaded content."
                onAdd={() => {
                  // eslint-disable-next-line no-unused-expressions
                  accessRequests.length > 0 ? navigate(Pathname.createGroup) : setSnackbar({
                    isVisible: true,
                    message: "You don't have viewers to create Groups",
                  });
                }}
                title={`${userDetails?.firstname ? `${userDetails?.firstname}'s ` : ''}Groups`}
              />
              <AddBox
                alignRight
                className={styles.individualViewerAddBox}
                description="Add Individual viewers to view your content."
                title="Individual Viewers"
                onAdd={() => {
                  updateAddViewerModalSettings({
                    description: 'We will automatically add the viewer to your authorized list of viewers',
                    title: 'Add New Viewer',
                  });
                  setAddViewerModalVisibility(true);
                }}
              />
              <div className={styles.tableContainer}>
                <Collapse timeout={400} in={!loadingGroups}>
                  <Fade timeout={400} in={!loadingGroups}>
                    <Table className={styles.table}>
                      <AddSuccessorModal />
                      <TableBody>
                        {groups?.length ? (
                          groups.map(({ title, id, members_count }) => (
                            <GroupRow
                              key={id}
                              title={title}
                              id={id}
                              members_count={members_count}
                              setSnackbar={setSnackbar}
                              onChange={() => fetchGroups()}
                            />
                          ))
                        ) : (
                          <TableRow>
                            <TableBodyCell
                              className={styles.tableBodyCell}
                              align="center"
                              colSpan="2"
                            >
                              No new groups
                            </TableBodyCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Fade>
                </Collapse>
                <Collapse timeout={400} in={loadingGroups}>
                  <Fade timeout={400} in={loadingGroups}>
                    <Table className={styles.table}>
                      <GroupTableHead />
                      <TableBody>
                        <TableRow>
                          <TableBodyCell align="center" className={styles.tableBodyCell} colSpan="2">
                            Loading...
                          </TableBodyCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Fade>
                </Collapse>
              </div>
              <div className={styles.addViewerBox}>
                {accessRequests && accessRequests.length > 0 ? (
                  <DialogContent
                    style={{
                      color: '#fff',
                      display: !accessRequests ? 'none' : accessRequests.length > 0 ? 'flex' : 'none',
                      flexDirection: 'column',
                      width: '100%',
                      marginBottom: '10px',
                    }}
                  >
                    {loadingAccessRequests
                      ? 'Getting User Connections ...'
                      : accessRequests?.map((data) => (
                        <PersonButton
                          key={data?.id}
                          fetchAccessRequests={fetchAccessRequests}
                          data={data}
                        />
                      ))}
                  </DialogContent>
                ) : (
                  <Button
                    className={styles.addViewerBoxButton}
                    isGray
                    onClick={() => {
                      updateAddViewerModalSettings({
                        description: 'We will automatically add the viewer to your authorized list of viewers',
                        title: 'Add New Viewer',
                      });
                      setAddViewerModalVisibility(true);
                    }}
                  >
                    Add Viewer
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.modalDescription}>{addViewerModalSettings.description}</div>
            <div className={styles.viewersCountContainer}>
              <div className={styles.viewersCountTitle}>Viewers</div>
              <div className={styles.viewersCount}>0 of 999</div>
            </div>
            <div className={styles.viewerDetailsInputGrid}>
              {viewerData.map(({ firstName, lastName, email }, idx) => (
                <InputRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  idx={idx}
                  onChange={handalData}
                  removeThisRow={handalRemoveThisRow}
                  defaultFirstName={firstName}
                  defaultLastName={lastName}
                  defaultEmail={email}
                />
              ))}
            </div>
          </>
        )}
      </Modal>
      <AddSuccessorModal secessionAccount={secessionAccount} setSecessionAccount={(e) => setSecessionAccount(e)} />
      <Snackbar
        {...snackbar}
        onClose={() => setSnackbar({ ...snackbar, message: '', isVisible: false })}
      />
    </>
  );
};
